import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { 
  FiSearch, 
  FiFilter, 
  FiEye, 
  FiTrash2, 
  FiX,
  FiPhone,
  FiMail,
  FiMapPin,
  FiCalendar,
  FiUser,
  FiBook,
  FiEdit2,
  FiDownload
} from 'react-icons/fi';
import api from '../../services/api';
import './AdminApplications.css';

const AdminApplications = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [examTypeFilter, setExamTypeFilter] = useState('');
  const [selectedApp, setSelectedApp] = useState(null);
  const [editingStatus, setEditingStatus] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [exams, setExams] = useState([]);

  useEffect(() => {
    fetchExams();
  }, []);

  useEffect(() => {
    fetchApplications();
  }, [search, statusFilter, examTypeFilter, pagination.page]);

  const fetchExams = async () => {
    try {
      const response = await api.get('/exams', { params: { limit: 100 } });
      setExams(response.data.exams || []);
    } catch (error) {
      console.error('Sınavlar yüklenemedi:', error);
      setExams([]);
    }
  };

  const fetchApplications = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);
      if (examTypeFilter) params.append('examId', examTypeFilter);
      params.append('page', pagination.page);
      params.append('limit', 10);

      const response = await api.get(`/applications?${params.toString()}`);
      setApplications(response.data.applications);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Başvurular yüklenemedi:', error);
      // Demo data
      setApplications([
        { 
          _id: '1', 
          studentName: 'Ahmet', 
          studentSurname: 'Yılmaz', 
          birthDate: '2010-05-15',
          tcNo: '12345678901',
          currentSchool: 'Atatürk Ortaokulu',
          currentGrade: '8',
          parentName: 'Mehmet Yılmaz',
          parentPhone: '05321234567',
          parentEmail: 'mehmet@email.com',
          address: 'Merkez Mah. 123 Sk.',
          city: 'İstanbul',
          district: 'Kadıköy',
          examType: 'LGS', 
          status: 'pending', 
          createdAt: new Date() 
        },
        { 
          _id: '2', 
          studentName: 'Ayşe', 
          studentSurname: 'Kaya',
          birthDate: '2008-03-20',
          tcNo: '98765432109',
          currentSchool: 'Cumhuriyet Lisesi',
          currentGrade: '11',
          parentName: 'Ali Kaya',
          parentPhone: '05559876543',
          parentEmail: 'ali@email.com',
          address: 'Yeni Mah. 456 Sk.',
          city: 'Ankara',
          district: 'Çankaya',
          examType: 'YKS', 
          status: 'approved', 
          createdAt: new Date() 
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/applications/${id}`, { status });
      toast.success('Durum güncellendi');
      fetchApplications();
      setEditingStatus(null);
    } catch (error) {
      toast.error('Güncelleme başarısız');
    }
  };

  const deleteApplication = async (id) => {
    if (!window.confirm('Bu başvuruyu silmek istediğinize emin misiniz?')) return;
    
    try {
      await api.delete(`/applications/${id}`);
      toast.success('Başvuru silindi');
      fetchApplications();
      setSelectedApp(null);
    } catch (error) {
      toast.error('Silme işlemi başarısız');
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { label: 'Bekliyor', class: 'badge-pending' },
      approved: { label: 'Onaylandı', class: 'badge-approved' },
      rejected: { label: 'Reddedildi', class: 'badge-rejected' },
      contacted: { label: 'İletişime Geçildi', class: 'badge-contacted' },
      registered: { label: 'Kayıt Yapıldı', class: 'badge-registered' }
    };
    const info = statusMap[status] || { label: status, class: '' };
    return <span className={`badge ${info.class}`}>{info.label}</span>;
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('tr-TR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const exportToCSV = () => {
    const headers = ['Ad', 'Soyad', 'Sınıf', 'Okul', 'Veli Tel', 'Veli Email', 'Sınav', 'Durum', 'Tarih'];
    const data = applications.map(app => [
      app.studentName,
      app.studentSurname,
      app.currentGrade,
      app.currentSchool,
      app.parentPhone,
      app.parentEmail,
      app.examType,
      app.status,
      formatDate(app.createdAt)
    ]);

    const csv = [headers, ...data].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'basvurular.csv';
    link.click();
  };

  return (
    <div className="admin-applications">
      {/* Filters */}
      <div className="filters-bar">
        <div className="search-box">
          <FiSearch />
          <input
            type="text"
            placeholder="Ad, soyad veya telefon ile ara..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="filter-group">
          <FiFilter />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">Tüm Durumlar</option>
            <option value="pending">Bekleyen</option>
            <option value="approved">Onaylanan</option>
            <option value="rejected">Reddedilen</option>
            <option value="contacted">İletişime Geçilen</option>
            <option value="registered">Kayıt Yapılan</option>
          </select>

          <select
            value={examTypeFilter}
            onChange={(e) => setExamTypeFilter(e.target.value)}
          >
            <option value="">Tüm Sınavlar</option>
            {exams.length > 0 ? (
              exams.map(exam => (
                <option key={exam._id} value={exam._id}>
                  {exam.title}
                </option>
              ))
            ) : (
              <>
                <option value="LGS">LGS</option>
                <option value="YKS">YKS</option>
                <option value="Genel">Genel</option>
              </>
            )}
          </select>
        </div>

        <button className="export-btn" onClick={exportToCSV}>
          <FiDownload />
          Dışa Aktar
        </button>
      </div>

      {/* Table */}
      <div className="applications-table-container">
        {loading ? (
          <div className="table-loading">
            <div className="spinner"></div>
            <p>Yükleniyor...</p>
          </div>
        ) : applications.length === 0 ? (
          <div className="table-empty">
            <FiUser />
            <p>Başvuru bulunamadı</p>
          </div>
        ) : (
          <table className="applications-table">
            <thead>
              <tr>
                <th>Öğrenci</th>
                <th>Veli İletişim</th>
                <th>Sınav</th>
                <th>Durum</th>
                <th>Tarih</th>
                <th>İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {applications.map((app) => (
                <tr key={app._id}>
                  <td>
                    <div className="student-cell">
                      <span className="student-name">{app.studentName} {app.studentSurname}</span>
                      <span className="student-school">{app.currentSchool} - {app.currentGrade}. Sınıf</span>
                    </div>
                  </td>
                  <td>
                    <div className="contact-cell">
                      <a href={`tel:${app.parentPhone}`}><FiPhone /> {app.parentPhone}</a>
                      <a href={`mailto:${app.parentEmail}`}><FiMail /> {app.parentEmail}</a>
                    </div>
                  </td>
                  <td>{app.examType}</td>
                  <td>
                    {editingStatus === app._id ? (
                      <select
                        value={app.status}
                        onChange={(e) => updateStatus(app._id, e.target.value)}
                        onBlur={() => setEditingStatus(null)}
                        autoFocus
                      >
                        <option value="pending">Bekliyor</option>
                        <option value="approved">Onaylandı</option>
                        <option value="rejected">Reddedildi</option>
                        <option value="contacted">İletişime Geçildi</option>
                        <option value="registered">Kayıt Yapıldı</option>
                      </select>
                    ) : (
                      <div className="status-cell" onClick={() => setEditingStatus(app._id)}>
                        {getStatusBadge(app.status)}
                        <FiEdit2 className="edit-icon" />
                      </div>
                    )}
                  </td>
                  <td>{formatDate(app.createdAt)}</td>
                  <td>
                    <div className="actions-cell">
                      <button 
                        className="action-btn view"
                        onClick={() => setSelectedApp(app)}
                        title="Detay"
                      >
                        <FiEye />
                      </button>
                      <button 
                        className="action-btn delete"
                        onClick={() => deleteApplication(app._id)}
                        title="Sil"
                      >
                        <FiTrash2 />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="pagination">
          <button
            disabled={pagination.page === 1}
            onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
          >
            Önceki
          </button>
          <span>{pagination.page} / {pagination.pages}</span>
          <button
            disabled={pagination.page === pagination.pages}
            onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
          >
            Sonraki
          </button>
        </div>
      )}

      {/* Detail Modal */}
      <AnimatePresence>
        {selectedApp && (
          <motion.div 
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedApp(null)}
          >
            <motion.div 
              className="modal-content"
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h2>Başvuru Detayı</h2>
                <button onClick={() => setSelectedApp(null)}><FiX /></button>
              </div>
              
              <div className="modal-body">
                <div className="detail-section">
                  <h3><FiUser /> Öğrenci Bilgileri</h3>
                  <div className="detail-grid">
                    <div><label>Ad Soyad</label><span>{selectedApp.studentName} {selectedApp.studentSurname}</span></div>
                    <div><label>TC No</label><span>{selectedApp.tcNo}</span></div>
                    <div><label>Doğum Tarihi</label><span>{formatDate(selectedApp.birthDate)}</span></div>
                    <div><label>Okul</label><span>{selectedApp.currentSchool}</span></div>
                    <div><label>Sınıf</label><span>{selectedApp.currentGrade}. Sınıf</span></div>
                  </div>
                </div>

                <div className="detail-section">
                  <h3><FiPhone /> Veli Bilgileri</h3>
                  <div className="detail-grid">
                    <div><label>Ad Soyad</label><span>{selectedApp.parentName}</span></div>
                    <div><label>Telefon</label><a href={`tel:${selectedApp.parentPhone}`}>{selectedApp.parentPhone}</a></div>
                    <div><label>E-posta</label><a href={`mailto:${selectedApp.parentEmail}`}>{selectedApp.parentEmail}</a></div>
                    {selectedApp.parentJob && <div><label>Meslek</label><span>{selectedApp.parentJob}</span></div>}
                  </div>
                </div>

                <div className="detail-section">
                  <h3><FiMapPin /> Adres Bilgileri</h3>
                  <div className="detail-grid">
                    <div><label>Adres</label><span>{selectedApp.address}</span></div>
                    <div><label>İl / İlçe</label><span>{selectedApp.city} / {selectedApp.district}</span></div>
                  </div>
                </div>

                <div className="detail-section">
                  <h3><FiBook /> Sınav Bilgileri</h3>
                  <div className="detail-grid">
                    <div><label>Sınav Türü</label><span>{selectedApp.examType}</span></div>
                    <div><label>Durum</label>{getStatusBadge(selectedApp.status)}</div>
                    <div><label>Başvuru Tarihi</label><span>{formatDate(selectedApp.createdAt)}</span></div>
                  </div>
                </div>

                {selectedApp.notes && (
                  <div className="detail-section">
                    <h3>Notlar</h3>
                    <p className="notes-text">{selectedApp.notes}</p>
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setSelectedApp(null)}>
                  Kapat
                </button>
                <button className="btn btn-primary" onClick={() => {
                  setEditingStatus(selectedApp._id);
                  setSelectedApp(null);
                }}>
                  Durumu Güncelle
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminApplications;

